import { isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Input } from '@/components/core/Input';
import { useFloatFormatter } from '@/hooks/useFloatFormatter';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { usePrecisionAndScale } from '../hooks/usePrecisionAndScale';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface FloatComponentProps extends EditorComponent {
    // Float specific props go here
    isDimensional?: boolean;
    className?: string;
}

const FloatComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    hideLabel,
    hidden = false,
    readonly = false,
    gridMode = false,
    isKeyAttribute = false,
    isDimensional = false,
    beforeAttributeControl,
    className,
    handleUrlChange,
    onBlur: parentOnBlur
}: FloatComponentProps) => {

    const { formatFloat } = useFloatFormatter();
    const { applyPrecisionAndScale } = usePrecisionAndScale(attribute);

    const focusRef = useFocusOnAttribute<HTMLInputElement>(attribute);

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
    });

    const [viewModel, setViewModel] = useState('');

    useEffect(() => {
        if (field.value) {
            const formattedValue = formatFloat(field.value.toString());
            const formattedWithPrecision = applyPrecisionAndScale(formattedValue);
            setViewModel(formattedWithPrecision);
        } else {
            setViewModel('');
        }
        //eslint-disable-next-line
    }, [field.value]);

    if (hidden) {
        return null;
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        let formattedValue = formatFloat(inputValue);
        formattedValue = applyPrecisionAndScale(formattedValue);

        setViewModel(formattedValue);

        const persistingValue = formattedValue.replace(',', '.');
        field.onChange(persistingValue);
    };

    const handleBlur = () => {
        field.onBlur();
        if (parentOnBlur) {
            parentOnBlur();
        }
    };

    const setRef = (e: HTMLInputElement | null) => {
        if (e) {
            (focusRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
        }
        field.ref(e);
    };

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly && !isDimensional}
        >
            <div className="tw-flex tw-flex-row">
                <Input
                    id={fieldPath}
                    borderless={gridMode}
                    placeholder={placeholder}
                    disabled={readonly}
                    value={viewModel}
                    onClick={handleUrlChange}
                    className={`${gridMode? '':'tw-h-[36px]'} ${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white ${className}`}
                    ref={setRef}
                    name={field.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    gridMode={gridMode}
                />
            </div>
        </Layout>
    );
};

export { FloatComponent };
